import type { ComponentProps } from 'react';
import React, { Fragment, useEffect } from 'react';
import styled from '@emotion/styled';
import type {
  BackgroundColorProps,
  BorderRadiusProps,
  GridColumnProps,
  WidthProps,
  ZIndexProps,
} from 'styled-system';
import { backgroundColor, borderRadius, gridColumn, space, width, zIndex } from 'styled-system';

import { CssGrid } from 'common/components/CssGrid';
import { Flex } from 'common/components/Flex';
import ProductCard from 'common/components/ProductCard';
import SkeletonLoader from 'common/components/SkeletonLoader';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import { listStyle, type ListStyleProps } from 'common/theme/system-utilities';
import { useObserveArticles } from 'common/tracking/useObserveArticles';
import type { Product } from 'common/types/product-card';
import { formatPrice } from 'common/utils/format-price';
import GlobalRibbon from '../../global-ribbon/components/GlobalRibbon';
import ListViewTeaser from '../../list-view-teaser/components/ListViewTeaser';
import RelatedSearch, {
  RelatedSearchPlacement,
} from '../../related-search/components/RelatedSearch';
import {
  getMinPrice,
  getPageNumberForIndex,
  useRelatedSearchPosition,
  useRibbonPosition,
} from '../utils';
import { LIST_VIEW_TEASER_POSITION } from './constants';
import Placeholder from './Placeholder';
import type { ProductCardListProps } from './types';

const itemWidth = [
  'calc((100% - 4px) / 2)',
  'calc((100% - 16px) / 3)',
  'calc((100% - 36px) / 4)',
  'calc((100% - 48px) / 4)',
];

const Container = styled(CssGrid)<ListStyleProps>`
  ${listStyle};
`;

const containerStyles: ComponentProps<typeof Container> = {
  mt: 'sp_0',
  mb: ['sp_16', 'sp_20', 'sp_24', 'sp_32'],
  gridGap: ['sp_4', 'sp_8', 'sp_12', 'sp_16'],
  gridTemplateColumns: [
    'repeat(2, minmax(0, 1fr))',
    'repeat(3, minmax(0, 1fr))',
    'repeat(4, minmax(0, 1fr))',
  ],
  listStyle: 'none',
  mx: 'sp_0',
  px: 'sp_0',
};

const ListItem = styled.article<WidthProps & BackgroundColorProps & BorderRadiusProps>`
  ${width};
  ${backgroundColor};
  ${borderRadius};
`;

const listItemStyles: ComponentProps<typeof ListItem> = {
  backgroundColor: 'sys.neutral.background.default',
  borderRadius: 'medium',
  width: '100%',
};

const RibbonContainer = styled.div<
  GridColumnProps & BackgroundColorProps & SpaceProps & BorderRadiusProps & ZIndexProps
>`
  ${gridColumn};
  ${backgroundColor};
  ${space};
  ${borderRadius};
  ${zIndex};
`;

const ribbonContainerStyles: ComponentProps<typeof RibbonContainer> = {
  gridColumn: '1 / -1',
};

const relatedSearchContainerStyles: ComponentProps<typeof RibbonContainer> = {
  gridColumn: '1 / -1',
  backgroundColor: 'sys.neutral.background.default',
  py: ['sp_16', null, null, 'sp_24'],
  px: [null, null, 'sp_16'],
  borderRadius: 'medium',
  zIndex: 0,
};

const Ribbon = styled(GlobalRibbon)<SpaceProps>`
  ${space};
`;

const SkeletonLoaderContainer = styled.div<GridColumnProps>`
  ${gridColumn};
`;

const skeletonLoaderContainerStyles: ComponentProps<typeof SkeletonLoaderContainer> = {
  gridColumn: '1 / -1',
};
const Skeleton = ({
  skeletonItemsCount,
  skeletonRef,
}: Pick<ProductCardListProps, 'skeletonItemsCount' | 'skeletonRef'>) => {
  if (!skeletonItemsCount) {
    return null;
  }

  return (
    <SkeletonLoaderContainer
      {...skeletonLoaderContainerStyles}
      {...(skeletonRef ? { ref: skeletonRef } : {})}
    >
      <SkeletonLoader
        ready={false}
        placeholder={<Placeholder itemsCount={skeletonItemsCount} itemWidth={itemWidth} />}
      />
    </SkeletonLoaderContainer>
  );
};

const getConstructorAttributes = ({
  parentSku,
  name,
  sku,
  prices,
  sponsored,
}: Pick<Product, 'parentSku' | 'name' | 'sku' | 'prices' | 'sponsored'>) => ({
  'data-cnstrc-item-id': parentSku,
  'data-cnstrc-item-name': name,
  'data-cnstrc-item-variation-id': sku,
  'data-cnstrc-item-price': formatPrice(prices.special?.value ?? prices.regular?.value ?? ''),
  ...(sponsored?.id ? { 'data-cnstrc-sl-campaign-id': sponsored.id } : {}),
  ...(sponsored?.owner ? { 'data-cnstrc-sl-campaign-owner': sponsored.owner } : {}),
});

interface ProductCardListPropsWithFocus extends ProductCardListProps {
  focusIndex?: number;
  resetFocus?: () => void;
  itemRefs?: React.MutableRefObject<Array<HTMLAnchorElement | null>>;
}

const InlineRelatedSearch = ({ items }: { items: ProductCardListProps['relatedSearchItems'] }) => {
  if (!items?.length) {
    return null;
  }

  return (
    <RibbonContainer {...relatedSearchContainerStyles} as="li">
      <RelatedSearch items={items} placement={RelatedSearchPlacement.PlpInline} />
    </RibbonContainer>
  );
};

const ProductCardList = ({
  skeletonItemsCount = 0,
  withSkeletonAbove = false,
  articles,
  productEventDataListType: listType,
  listViewRibbon,
  listViewTeaser,
  relatedSearchItems,
  page,
  pageSize,
  visibleProductsCount,
  isMarketplace = false,
  skeletonRef,
  focusIndex,
  resetFocus,
  itemRefs,
}: ProductCardListPropsWithFocus) => {
  useObserveArticles({
    articles: articles.map(a => ({ ...a, hasMinPrice: !!getMinPrice(a) })),
    listType,
  });
  const ribbonPosition = useRibbonPosition();
  const relatedSearchPosition = useRelatedSearchPosition(!!listViewTeaser);
  const { t } = useLocalization();

  useEffect(() => {
    if (typeof focusIndex === 'number' && itemRefs?.current && itemRefs.current[focusIndex]) {
      itemRefs.current[focusIndex]?.focus();
      resetFocus?.();
    }
  }, [focusIndex, articles.length, resetFocus, itemRefs]);

  return (
    <Container
      as="ul"
      role="list"
      data-testid="list-view-inner-container"
      aria-label={t('h24_results')}
      {...containerStyles}
    >
      {withSkeletonAbove && <Skeleton skeletonItemsCount={skeletonItemsCount} />}
      {articles.map((article, index) => {
        const isListViewTeaser = index + 1 === LIST_VIEW_TEASER_POSITION && !!listViewTeaser;
        const isRibbon = index + 1 === ribbonPosition && !!listViewRibbon;
        const isRelatedSearch = index + 1 === relatedSearchPosition;
        const refs = itemRefs;

        return (
          <Fragment key={article.sku}>
            <ListItem
              {...listItemStyles}
              {...getConstructorAttributes(article)}
              data-testid="product-item"
              data-sku={article.sku}
              as="li"
              role="listitem"
            >
              <ProductCard
                ref={el => {
                  if (refs) refs.current[index] = el;
                }}
                article={article}
                index={index}
                pageNumber={getPageNumberForIndex({
                  index,
                  articlesCount: articles.length,
                  page,
                  pageSize,
                })}
                productEventDataListType={listType}
                withScrollOverload
                visibleProductsCount={visibleProductsCount}
                isMarketplace={isMarketplace}
                withProminentPricing
              />
            </ListItem>
            {isListViewTeaser && (
              <Flex as="li">
                <ListViewTeaser {...listViewTeaser} isRounded />
              </Flex>
            )}
            {isRibbon && (
              <RibbonContainer {...ribbonContainerStyles} as="li">
                <Ribbon mb="sp_0" {...listViewRibbon} isRounded />
              </RibbonContainer>
            )}
            {isRelatedSearch && <InlineRelatedSearch items={relatedSearchItems} />}
          </Fragment>
        );
      })}
      {!withSkeletonAbove && (
        <Skeleton skeletonItemsCount={skeletonItemsCount} skeletonRef={skeletonRef} />
      )}
    </Container>
  );
};

ProductCardList.displayName = 'ProductCardList';

export default ProductCardList;
